import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useContext, useMemo, useRef, useState } from 'react';
import type { AdyenPaymentCompletedResult } from 'src/components/AdyenPayment/AdyenDropIn';
import { AdyenDropIn } from 'src/components/AdyenPayment/AdyenDropIn';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import { PaymentMethodCard } from 'src/components/PaymentMethod';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { typography } from 'src/core/themes/shared.css';
import { Button, Radio, RadioGroup, Stack } from 'src/core/ui';
import {
  useCreateAdyenPaymentSession,
  useSubmitAdyenPaymentResult,
  useSubmitPaymentWithStoredMethod,
} from 'src/hooks/api/useAdyenPaymentSession';
import { useApplyExternalPayment } from 'src/hooks/api/useApplyExternalPayment';
import { useGetBuyerPaymentMethods } from 'src/hooks/api/useGetBuyerPaymentMethods';
import { SALES_GROUP_QUERY_KEY } from 'src/hooks/api/useGetSalesGroup';
import { useKnownCountries } from 'src/hooks/api/useKnownCountries';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { DetailSection } from 'src/modals/common/DetailSection';
import type { ModalProps } from 'src/modals/Modal';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalFooter,
} from 'src/modals/Modal/Modal.styled';
import { ModalHeader } from 'src/modals/Modal/ModalHeader';
import { ExternalPaymentSection } from 'src/modals/RetailSale/components/ExternalPaymentSection';
import { PartialPaymentCoverageDialog } from 'src/modals/RetailSale/components/PartialPaymentCoverageDialog';
import { PartialPaymentInput } from 'src/modals/RetailSale/components/PartialPaymentInput';
import * as retailStyles from 'src/modals/RetailSale/components/RetailSaleCheckoutStep.css';
import { getPaymentCountryCode } from 'src/modals/RetailSale/RetailSaleLocalContext.utils';
import { ContentId } from 'src/utils/constants/contentId';
import { ExternalPaymentMethodType } from 'src/WebApiController';
import { Feature } from 'src/WebApiController';
import { SaleLineItemCoverage } from 'src/WebApiController';

import * as styles from './MarketplacePaymentDialog.css';

/**
 * Sentinel RadioGroup value representing the "add new card" state (i.e. no
 * stored payment method selected). RadioGroup values must be strings, and
 * `selectedStoredPaymentMethodId === null` maps to this value.
 */
const ADD_NEW_CARD_VALUE = '__add_new__';

type ModalConfig = Omit<ModalProps, 'fullscreen'>;

type PayState = 'configuring' | 'paying';

type PaymentMethodChoice = 'card' | 'external';

type AdyenSessionState = {
  sessionId: string;
  sessionData: string;
  clientKey: string;
  environment: string;
  countryCode: string | null;
  lineItemCoverages: SaleLineItemCoverage[] | null;
};

type GroupMarketplacePaymentDialogProps = {
  /** The sales group being paid down. Every Adyen call is keyed on this. */
  salesGroupId: string;
  /** Outstanding balance (group.balanceDue?.amt). Used as the full-payment amount. */
  remainingAmount: number;
  currencyCode: string;
  buyerEmail: string | null;
  /**
   * First non-cancelled member sale's id (fallback: first member). Used only for
   * the saleId-keyed ApplyExternalPayment endpoint — see the caveat comment below.
   */
  representativeSaleId: number;
  cancelTo: ModalConfig | null;
  initialPayState?: PayState;
  initialAdyenSession?: AdyenSessionState | null;
};

/**
 * Group-level variant of MarketplacePaymentDialog (POS-8632). Pays down an
 * EXISTING sales group's balance, reusing the exact payment mechanics of the
 * single-sale "Pay Now" dialog: the same leaf components (ExternalPaymentSection,
 * PartialPaymentInput, PartialPaymentCoverageDialog) and the same hooks. It
 * differs only in where its inputs come from (props sourced from
 * SalesGroupDetails rather than a SaleDetails) and in what it invalidates on
 * success (the group query keys instead of getRetailSaleDetails + setActivePosEntity).
 */
export const GroupMarketplacePaymentDialog = ({
  salesGroupId,
  remainingAmount,
  currencyCode,
  buyerEmail,
  representativeSaleId,
  cancelTo,
  initialPayState = 'configuring',
  initialAdyenSession = null,
}: GroupMarketplacePaymentDialogProps) => {
  const queryClient = useQueryClient();
  const { setModal, closeModal, currentModalConfig } = useContext(ModalContext);
  const { showErrorDialog } = useClientLogContext();

  const { data: knownCountries } = useKnownCountries();
  const countryCode = getPaymentCountryCode(undefined, knownCountries);

  const createPaymentSession = useCreateAdyenPaymentSession();
  const submitPaymentResult = useSubmitAdyenPaymentResult();
  const submitPaymentWithStoredMethod = useSubmitPaymentWithStoredMethod();
  const applyExternalPayment = useApplyExternalPayment();

  const { data: savedPaymentMethods = [] } =
    useGetBuyerPaymentMethods(buyerEmail);

  // Gate the payment-mode radio behind the RetailSale feature flag, mirroring
  // the group-creation payment step (RetailSalePaymentSection).
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const paymentMethodMsg = useContent(ContentId.PaymentMethod);

  // ── Shared state ──────────────────────────────────────────────────────────
  const [paymentMethodChoice, setPaymentMethodChoice] =
    useState<PaymentMethodChoice>('card');
  const [payState, setPayState] = useState<PayState>(initialPayState);
  const [adyenSession, setAdyenSession] = useState<AdyenSessionState | null>(
    initialAdyenSession ?? null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isPartialPayment, setIsPartialPayment] = useState(false);
  const [partialAmount, setPartialAmount] = useState(remainingAmount);
  const [selectedStoredPaymentMethodId, setSelectedStoredPaymentMethodId] =
    useState<string | null>(null);
  const merchantRef = useRef<string | null>(null);

  // ── External payment state (useState, no react-hook-form) ─────────────────
  const [externalPaymentMethodType, setExternalPaymentMethodType] = useState<
    ExternalPaymentMethodType | undefined
  >(undefined);
  const [externalPaymentNote, setExternalPaymentNote] = useState<string>('');
  const [externalPaymentDate, setExternalPaymentDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [externalMethodTypeError, setExternalMethodTypeError] = useState<
    string | undefined
  >(undefined);

  const handlePaymentChoiceChange = (value: string) => {
    setPaymentMethodChoice(value as PaymentMethodChoice);
    // Reset external validation error when switching modes
    setExternalMethodTypeError(undefined);
  };

  const handleClose = () => {
    setAdyenSession(null);
    merchantRef.current = null;
    if (cancelTo) {
      setModal(cancelTo);
    } else {
      closeModal(true);
    }
  };

  // ── Invalidate and refresh the sales group after a payment ────────────────
  // Group variant: invalidate BOTH the group payload and its member-sales list.
  // Do NOT call setActivePosEntity (this modal is not driven by a single
  // active SaleDetails entity).
  const invalidateAndRefresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
    });
    await queryClient.invalidateQueries({
      queryKey: ['getSalesInSalesGroup', salesGroupId],
    });
  };

  // ── External partial: coverage confirmed ─────────────────────────────────
  const handleExternalCoverageConfirmed = async (
    coverages: SaleLineItemCoverage[]
  ) => {
    setIsLoading(true);
    try {
      // POS-8632: ApplyExternalPayment is saleId-keyed; for multi-member groups an external partial payment is attributed to the representative member sale (coverages still span all group lines). Known caveat — Adyen paths are group-keyed and unaffected.
      await applyExternalPayment.mutateAsync({
        saleId: representativeSaleId,
        input: {
          externalPaymentMethodType: externalPaymentMethodType!,
          externalPaymentNote: externalPaymentNote || undefined,
          externalPaymentAmount: partialAmount,
          externalPaymentDate: externalPaymentDate
            ? new Date(externalPaymentDate).toISOString()
            : undefined,
          coverages,
        },
      });
      await invalidateAndRefresh();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = async () => {
    if (!merchantRef.current) {
      merchantRef.current = `Retail-${representativeSaleId}-${Date.now()}`;
    }

    // ── External payment flow ─────────────────────────────────────────────
    if (paymentMethodChoice === 'external') {
      // Validate that a method type has been selected
      if (externalPaymentMethodType == null) {
        setExternalMethodTypeError('Payment Method Type is required');
        return;
      }
      setExternalMethodTypeError(undefined);

      if (isPartialPayment) {
        // External + partial: open coverage dialog, then call ApplyExternalPayment
        const thisDialogConfig = currentModalConfig;
        setModal({
          children: (
            <PartialPaymentCoverageDialog
              salesGroupId={salesGroupId}
              partialAmount={partialAmount}
              currencyCode={currencyCode}
              onConfirm={handleExternalCoverageConfirmed}
              onCancel={() => {
                if (thisDialogConfig) {
                  setModal(thisDialogConfig);
                } else {
                  closeModal(true);
                }
              }}
            />
          ),
        });
        return;
      }

      // External + full: call ApplyExternalPayment directly
      setIsLoading(true);
      try {
        // POS-8632: ApplyExternalPayment is saleId-keyed; for multi-member groups an external partial payment is attributed to the representative member sale (coverages still span all group lines). Known caveat — Adyen paths are group-keyed and unaffected.
        await applyExternalPayment.mutateAsync({
          saleId: representativeSaleId,
          input: {
            externalPaymentMethodType: externalPaymentMethodType,
            externalPaymentNote: externalPaymentNote || undefined,
            externalPaymentAmount: remainingAmount,
            externalPaymentDate: externalPaymentDate
              ? new Date(externalPaymentDate).toISOString()
              : undefined,
            coverages: undefined,
          },
        });
        await invalidateAndRefresh();
        merchantRef.current = null;
        handleClose();
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // ── Adyen flow (card) — group-keyed on salesGroupId ───────────────────
    if (isPartialPayment) {
      // Capture values before navigating to coverage dialog (component unmounts)
      const thisDialogConfig = currentModalConfig;
      const capturedStoredMethodId = selectedStoredPaymentMethodId;
      const capturedMerchantRef = merchantRef.current;
      setModal({
        children: (
          <PartialPaymentCoverageDialog
            salesGroupId={salesGroupId}
            partialAmount={partialAmount}
            currencyCode={currencyCode}
            onConfirm={async (coverages) => {
              setIsLoading(true);
              try {
                if (capturedStoredMethodId) {
                  await submitPaymentWithStoredMethod.mutateAsync({
                    salesGroupId,
                    storedPaymentMethodId: capturedStoredMethodId,
                    amount: partialAmount,
                    currencyCode,
                    buyerEmail,
                    reference: capturedMerchantRef,
                    lineItemCoverages: coverages,
                  });
                  await invalidateAndRefresh();
                  handleClose();
                } else {
                  const session = await createPaymentSession.mutateAsync({
                    salesGroupId,
                    amount: partialAmount,
                    currencyCode,
                    buyerEmail,
                    returnUrl: window.location.href,
                    reference: capturedMerchantRef,
                    countryCode,
                    lineItemCoverages: coverages,
                  });
                  setModal({
                    children: (
                      <GroupMarketplacePaymentDialog
                        salesGroupId={salesGroupId}
                        remainingAmount={remainingAmount}
                        currencyCode={currencyCode}
                        buyerEmail={buyerEmail}
                        representativeSaleId={representativeSaleId}
                        cancelTo={cancelTo}
                        initialPayState="paying"
                        initialAdyenSession={{
                          ...session,
                          countryCode,
                          lineItemCoverages: coverages,
                        }}
                      />
                    ),
                  });
                }
              } finally {
                setIsLoading(false);
              }
            }}
            onCancel={() => {
              if (thisDialogConfig) {
                setModal(thisDialogConfig);
              } else {
                closeModal(true);
              }
            }}
          />
        ),
      });
      return;
    }
    // Full payment (Adyen)
    setIsLoading(true);
    try {
      if (selectedStoredPaymentMethodId) {
        await submitPaymentWithStoredMethod.mutateAsync({
          salesGroupId,
          storedPaymentMethodId: selectedStoredPaymentMethodId,
          amount: remainingAmount,
          currencyCode,
          buyerEmail,
          reference: merchantRef.current,
          lineItemCoverages: null,
        });
        await invalidateAndRefresh();
        merchantRef.current = null;
        handleClose();
      } else {
        const session = await createPaymentSession.mutateAsync({
          salesGroupId,
          amount: remainingAmount,
          currencyCode,
          buyerEmail,
          returnUrl: window.location.href,
          reference: merchantRef.current,
          countryCode,
          lineItemCoverages: null,
        });
        setAdyenSession({
          ...session,
          countryCode,
          lineItemCoverages: null,
        });
        setPayState('paying');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onPaymentCompleted = async (result: AdyenPaymentCompletedResult) => {
    if (!adyenSession) return;
    if (result.resultCode !== 'Authorised') {
      showErrorDialog('AdyenPayment', {
        message: `Payment not authorized: ${result.resultCode}`,
      });
      setAdyenSession(null);
      setPayState('configuring');
      return;
    }
    setIsLoading(true);
    try {
      await submitPaymentResult.mutateAsync({
        salesGroupId,
        input: {
          resultCode: result.resultCode,
          sessionResult: result.sessionResult ?? null,
          merchantReference: merchantRef.current ?? null,
          pspReference: null,
          lineItemCoverages: adyenSession.lineItemCoverages,
        },
      });
      await invalidateAndRefresh();
      merchantRef.current = null;
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const isProceedDisabled = useMemo(() => {
    if (isLoading) return true;
    if (
      isPartialPayment &&
      (partialAmount <= 0 || partialAmount >= remainingAmount)
    )
      return true;
    return false;
  }, [isLoading, isPartialPayment, partialAmount, remainingAmount]);

  const isExternalPayment = paymentMethodChoice === 'external';

  return (
    <>
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerBar}>
          <h5 className={clsx(typography.title5, styles.headerText)}>
            <Content id={ContentId.AddPayment} />
          </h5>
        </div>
      </ModalHeader>
      <ModalBody>
        <ModalBodyDataContainer style={{ height: '100%' }}>
          {payState === 'configuring' && (
            <DetailSection name={paymentMethodMsg}>
              <Stack direction="column" gap="m">
                {/* Top-level payment method radio — gated behind RetailSale,
                    matching the group-creation payment step. */}
                {hasRetailSaleFeature && (
                  <RadioGroup
                    value={paymentMethodChoice}
                    onValueChange={handlePaymentChoiceChange}
                    disabled={isLoading}
                    className={styles.radioGroup}
                  >
                    <Radio
                      value="card"
                      label="Paid in POS"
                      disabled={isLoading}
                    />
                    <Radio
                      value="external"
                      label="Paid Externally (Cash / Wire / Other)"
                      disabled={isLoading}
                    />
                  </RadioGroup>
                )}

                {/* Partial payment toggle — visible for BOTH card and external flows */}
                <PartialPaymentInput
                  disabled={isLoading}
                  totalAmount={remainingAmount}
                  currencyCode={currencyCode}
                  isPartialPayment={isPartialPayment}
                  partialAmount={partialAmount}
                  onIsPartialPaymentChange={setIsPartialPayment}
                  onPartialAmountChange={setPartialAmount}
                />

                {/* Card (Adyen) flow — saved cards + add new card */}
                {!isExternalPayment && (
                  <RadioGroup
                    className={retailStyles.savedCardsSection}
                    value={selectedStoredPaymentMethodId ?? ADD_NEW_CARD_VALUE}
                    disabled={isLoading}
                  >
                    {savedPaymentMethods.map((card) => {
                      const cardId = card.storedPaymentMethodId;
                      const isSelected =
                        selectedStoredPaymentMethodId === cardId;
                      return (
                        <div
                          key={cardId}
                          role="radio"
                          aria-checked={isSelected}
                          tabIndex={0}
                          className={clsx(retailStyles.savedCardRow, {
                            [retailStyles.savedCardRowSelected]: isSelected,
                          })}
                          onClick={
                            isLoading
                              ? undefined
                              : () => setSelectedStoredPaymentMethodId(cardId)
                          }
                          onKeyDown={
                            isLoading
                              ? undefined
                              : (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    setSelectedStoredPaymentMethodId(cardId);
                                  }
                                }
                          }
                        >
                          <Radio
                            value={cardId}
                            checked={isSelected}
                            disabled={isLoading}
                            label=""
                            aria-hidden
                            tabIndex={-1}
                          />
                          <PaymentMethodCard
                            cardBrand={card.cardBrand}
                            cardSummary={card.cardSummary}
                            expiryMonth={card.expiryMonth}
                            expiryYear={card.expiryYear}
                            isDefault={card.isDefault}
                          />
                        </div>
                      );
                    })}
                    <Button
                      type="button"
                      variant="textPlain"
                      shape="none"
                      justifyContent="start"
                      disabled={isLoading}
                      onClick={() => setSelectedStoredPaymentMethodId(null)}
                      className={clsx(retailStyles.addNewCardRow, {
                        [retailStyles.addNewCardRowSelected]:
                          selectedStoredPaymentMethodId === null,
                      })}
                    >
                      <Radio
                        value={ADD_NEW_CARD_VALUE}
                        checked={selectedStoredPaymentMethodId === null}
                        label=""
                        aria-hidden
                        tabIndex={-1}
                      />
                      <Content id={ContentId.AddNewCard} />
                    </Button>
                  </RadioGroup>
                )}

                {/* External payment form — controlled mode (no react-hook-form) */}
                {isExternalPayment && (
                  <ExternalPaymentSection
                    disabled={isLoading}
                    totalAmount={
                      isPartialPayment ? partialAmount : remainingAmount
                    }
                    controlled={{
                      methodType: externalPaymentMethodType,
                      note: externalPaymentNote,
                      date: externalPaymentDate,
                      currencyCode,
                      methodTypeError: externalMethodTypeError,
                      onMethodTypeChange: setExternalPaymentMethodType,
                      onNoteChange: setExternalPaymentNote,
                      onDateChange: setExternalPaymentDate,
                    }}
                  />
                )}
              </Stack>
            </DetailSection>
          )}

          {payState === 'paying' && adyenSession && (
            <div className={styles.adyenContainer}>
              <AdyenDropIn
                sessionId={adyenSession.sessionId}
                sessionData={adyenSession.sessionData}
                clientKey={adyenSession.clientKey}
                environment={adyenSession.environment}
                countryCode={adyenSession.countryCode}
                onPaymentCompleted={onPaymentCompleted}
                onError={(error) => {
                  showErrorDialog('AdyenPayment', { message: error.message });
                  setAdyenSession(null);
                  setPayState('configuring');
                }}
              />
            </div>
          )}
        </ModalBodyDataContainer>
      </ModalBody>
      <ModalFooter>
        <Stack justifyContent="end" gap="m">
          <CancelButton onClick={handleClose} disabled={isLoading} />
          {payState === 'configuring' && (
            <OkButton
              textContentId={ContentId.Confirm}
              variant="regular"
              onClick={handleProceed}
              disabled={isProceedDisabled}
            />
          )}
        </Stack>
      </ModalFooter>
    </>
  );
};
